import { create } from "zustand";
import { EventPriority, EventType } from "../types/Event";
import useStoreRoomSession from "./useStoreRoomSession";

type Event = {
    name: string;
    type: EventType;
    priority: EventPriority;
    payload: string;
    attempts?: number;
}

type StateStoreEvent = {
    events: Array<Event>,
}

type StoreEventActions = {
    enqueue: (name: string, type: EventType, payload: unknown, priority: EventPriority, attempts?: number) => void,
    peek: (type: EventType) => Event | void,
    dequeue: (type: EventType) => Event | void,
    replace: (name: string, payload: unknown) => void,
    requeue: (name: string, type: EventType, payload: unknown, priority: EventPriority) => void,
    reset: () => void,
}

const PRIORITY_VALUES: Record<EventPriority, number> = { high: 0, medium: 1, low: 2 };

const findNextIndex = (events: Array<Event>, type: EventType): number => {
    let bestIndex = -1;

    for(let i = 0; i < events.length; i++) {
        if(events[i].type !== type) {
            continue;
        }

        if(bestIndex === -1 || PRIORITY_VALUES[events[i].priority] < PRIORITY_VALUES[events[bestIndex].priority]) {
            bestIndex = i;
        }
    }

    return bestIndex;
};

const useStoreEvent = create<StateStoreEvent & StoreEventActions>((set, get) => ({
    events: [],

    enqueue: (name: string, type: EventType, payload: unknown, priority: EventPriority, attempts?: number) => {
        set((state) => ({ ...state, events: [...state.events, { name, type, priority, payload: payload as string, attempts }] }), false);
    },

    peek: (type: EventType) => {
        const index = findNextIndex(get().events, type);

        return index === -1 ? undefined : get().events[index];
    },

    dequeue: (type: EventType) => {
        const { events } = get();
        const bestIndex = findNextIndex(events, type);

        if(bestIndex === -1) {
            return;
        }

        const event = events[bestIndex];

        set(state => ({ ...state, events: state.events.filter((_, i) => i !== bestIndex) }));

        return event;
    },

    replace: (name: string, payload: unknown) => {
        set((state) => ({
            ...state,
            events: state.events.map(e => e.name === name ? { name, type: e.type, payload: payload as string, priority: e.priority } : e)
        }));
    },

    requeue: (name: string, type: EventType, payload: unknown, priority: EventPriority) => {
        set((state) => ({
            ...state,
            events: [...state.events.filter(e => e.name !== name), { name, type, priority, payload: payload as string }]
        }), false);
    },

    reset: () => set(() => ({ events: [] })),
}));

useStoreRoomSession.subscribe(
    (state) => state.roomId,
    () => {
        useStoreEvent.getState().reset();
    }
);

export default useStoreEvent;
